export interface ArticleSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface ArticleContent {
  title: string;
  desc: string;
  category: string;
  intro: string[];
  sections: ArticleSection[];
  faq: { q: string; a: string }[];
  ctaTitle: string;
  ctaDesc: string;
}

export const articleContent: Record<string, Record<string, ArticleContent>> = {
  en: {
    'usfans-spreadsheet-guide': {
      title: 'USFans Spreadsheet Guide: Organize & Shop the Full Catalog',
      desc: 'A complete guide to the USFans Spreadsheet — how it organizes fashion categories, how to browse efficiently, and smart shopping tips.',
      category: 'Complete Guide',
      intro: [
        'Shopping across a large fashion catalog can feel overwhelming, especially when products are spread across dozens of pages. The USFans Spreadsheet was created to solve exactly that problem: it brings shoes, hoodies, t-shirts, jackets, jerseys, accessories and more into one organized, easy-to-navigate overview.',
        'In this guide, we explain what the USFans Spreadsheet is, how its category system works, and how you can use it to find the right item faster — without endless scrolling or guesswork.',
      ],
      sections: [
        {
          heading: 'What Is the USFans Spreadsheet?',
          paragraphs: [
            'The USFans Spreadsheet is a structured catalog that groups a wide range of fashion and fan merchandise into clear, logical categories. Rather than browsing a single unorganized product stream, visitors can jump directly to the category they care about.',
          ],
          bullets: [
            'Shoes — sneakers, casual footwear and athletic styles',
            'Hoodies/Sweaters — cozy pullovers, hoodies and knitwear',
            'Jersey — team and sports-inspired jerseys',
            'Accessories — small items that complete an outfit',
          ],
        },
        {
          heading: 'How to Browse Efficiently',
          paragraphs: [
            'Start by identifying what you actually need. Use the category dropdown or the category buttons on the homepage to jump straight to the section you want. If you are just exploring, browse the popular categories first.',
          ],
        },
        {
          heading: 'Smart Shopping Tips',
          paragraphs: [
            'Double-check product details, sizes and availability before ordering. Review shipping and return information on the store itself, and keep your payment details private.',
          ],
        },
      ],
      faq: [
        { q: 'What is the USFans Spreadsheet?', a: 'It is an organized catalog that groups fashion and fan merchandise into clear categories, making it faster to find what you need.' },
        { q: 'How do I find a specific product quickly?', a: 'Use the category dropdown or the category buttons on the homepage to jump straight to the section you need.' },
        { q: 'Is the USFans Spreadsheet a store?', a: 'It is an informational and navigation resource. Purchases are completed on the main store it links to.' },
      ],
      ctaTitle: 'Ready to Browse the Full Catalog?',
      ctaDesc: 'Now that you understand how the USFans Spreadsheet works, explore the categories or visit the store directly to start shopping.',
    },
    'usfans-shipping-cost': {
      title: 'USFans Shipping Cost: What You Should Know Before You Order',
      desc: 'A practical guide to USFans shipping costs — how shipping works, what affects the price, and how to avoid surprises at checkout.',
      category: 'Shipping Guide',
      intro: [
        'Shipping cost is one of the most common questions people ask before placing an order, and for good reason — it directly affects the final price you pay. Understanding how shipping works can help you plan your budget and avoid surprises at checkout.',
        'This guide explains the factors that influence shipping costs, how shipping methods differ, and a few simple steps you can take to keep delivery costs under control.',
      ],
      sections: [
        {
          heading: 'What Affects Shipping Cost?',
          paragraphs: [
            'Several factors influence how much you pay for shipping, including the destination country, package weight and size, the shipping method you choose, and any current promotions or discounts.',
          ],
        },
        {
          heading: 'Shipping Methods Explained',
          paragraphs: [
            'Most stores offer a range of methods, from standard economy shipping to faster express options. Faster methods usually cost more but arrive sooner, so the right choice depends on how quickly you need your order.',
          ],
        },
        {
          heading: 'How to Keep Costs Low',
          paragraphs: [
            'Consolidate multiple items into a single order when possible, keep an eye out for free-shipping thresholds, and compare shipping options at checkout before finalizing your purchase.',
          ],
        },
      ],
      faq: [
        { q: 'How is shipping cost calculated?', a: 'Shipping cost depends on destination, package weight and size, the method you select, and any active promotions.' },
        { q: 'Can I reduce my shipping cost?', a: 'Yes — consolidate items into one order, look for free-shipping thresholds, and compare methods at checkout.' },
        { q: 'Where can I find the exact shipping price?', a: 'The exact price is shown at checkout on the store, based on your delivery address and selected method.' },
      ],
      ctaTitle: 'Plan Your Order With Confidence',
      ctaDesc: 'Understand your shipping options before you buy. Browse the catalog or visit the store to see current shipping details.',
    },
    'usfans-coupon': {
      title: 'USFans Coupon Guide: How Discounts and Savings Work',
      desc: 'Learn how USFans coupons and discounts work, where to look for savings, and how to apply them correctly at checkout.',
      category: 'Savings Guide',
      intro: [
        'Coupons are a popular way to save money when shopping online, but they only help if you know where to find them and how to use them correctly. Understanding how discounts work can save you real money on your next order.',
        'This guide explains how coupons typically work, where to look for valid savings, and a few common mistakes to avoid so you do not miss out on a discount.',
      ],
      sections: [
        {
          heading: 'How Coupons Work',
          paragraphs: [
            'A coupon is usually a code that you enter at checkout to receive a discount, such as a percentage off or a fixed amount off your order. The discount is applied before you complete the purchase.',
          ],
        },
        {
          heading: 'Where to Find Valid Savings',
          paragraphs: [
            'Savings are often shared through official store announcements, newsletters, and seasonal promotions. Always verify that a coupon is current and valid before relying on it.',
          ],
        },
        {
          heading: 'Common Coupon Mistakes',
          paragraphs: [
            'The most common mistakes are entering an expired code, applying a coupon to excluded items, or forgetting the minimum purchase requirement. Reading the terms carefully avoids these issues.',
          ],
        },
      ],
      faq: [
        { q: 'How do I apply a coupon?', a: 'Enter the coupon code in the designated field at checkout, and the discount will be applied before you complete the purchase.' },
        { q: 'Where can I find current coupons?', a: 'Look for savings through official store announcements, newsletters, and seasonal promotions.' },
        { q: 'Why did my coupon not work?', a: 'It may be expired, apply only to certain items, or require a minimum purchase. Check the coupon terms carefully.' },
      ],
      ctaTitle: 'Make the Most of Your Savings',
      ctaDesc: 'Know how discounts work before you shop. Explore the catalog and watch for valid savings on the store.',
    },
    'usfans-discount-code': {
      title: 'USFans Discount Code Guide: Save More at Checkout',
      desc: 'A clear guide to USFans discount codes — what they are, how they differ from coupons, and how to use them correctly.',
      category: 'Savings Guide',
      intro: [
        'Discount codes are a simple but powerful way to lower the cost of your order. Understanding the difference between a discount code and other savings can help you choose the best option for your purchase.',
        'This guide covers what discount codes are, how to enter them correctly, and the steps to take if a code does not seem to work.',
      ],
      sections: [
        {
          heading: 'What Is a Discount Code?',
          paragraphs: [
            'A discount code is a short text string that unlocks a specific discount at checkout, such as a percentage off or free shipping. It is usually entered once during the payment step.',
          ],
        },
        {
          heading: 'Discount Code vs. Coupon',
          paragraphs: [
            'The terms are often used interchangeably, but a discount code typically refers to the alphanumeric code itself, while a coupon may describe the broader promotion. Both work in similar ways at checkout.',
          ],
        },
        {
          heading: 'How to Apply It Correctly',
          paragraphs: [
            'Copy the code exactly as shown, enter it in the correct field, and confirm the discount appears before you pay. If nothing changes, check the code for typos or expired conditions.',
          ],
        },
      ],
      faq: [
        { q: 'What is a discount code?', a: 'It is a text code that unlocks a specific discount at checkout, such as a percentage off or free shipping.' },
        { q: 'How is it different from a coupon?', a: 'The two terms overlap, but a discount code usually refers to the code itself, while a coupon describes the broader promotion.' },
        { q: 'What if my code does not work?', a: 'Check for typos, expiration, and whether the code applies to the items in your cart.' },
      ],
      ctaTitle: 'Unlock Your Savings',
      ctaDesc: 'Understand discount codes before you order. Browse the catalog and apply valid codes at checkout.',
    },
    'usfans-warehouse': {
      title: 'USFans Warehouse Guide: How Orders Are Stored and Shipped',
      desc: 'Learn how USFans warehouse and fulfillment works, how orders are processed, and what it means for delivery times.',
      category: 'Shipping Guide',
      intro: [
        'The term warehouse refers to the facility where products are stored before they are packed and shipped to customers. Understanding how this process works can help you set realistic expectations for delivery.',
        'This guide explains what a warehouse does, how orders move from storage to your door, and what factors can affect processing and shipping time.',
      ],
      sections: [
        {
          heading: 'What Does a Warehouse Do?',
          paragraphs: [
            'A warehouse is where inventory is kept, organized, and prepared for shipment. When you place an order, items are located, packed, and handed to a carrier for delivery.',
          ],
        },
        {
          heading: 'How Orders Are Processed',
          paragraphs: [
            'After checkout, your order enters a processing stage where items are picked and packed. Once packed, the order is shipped and tracking information is typically provided.',
          ],
        },
        {
          heading: 'What Affects Delivery Time',
          paragraphs: [
            'Processing time, shipping method, destination distance, and carrier conditions all affect how long delivery takes. Understanding these factors helps you plan ahead.',
          ],
        },
      ],
      faq: [
        { q: 'What is a warehouse?', a: 'It is the facility where products are stored, packed, and prepared for shipment to customers.' },
        { q: 'How long does order processing take?', a: 'Processing time varies by store and order volume; tracking information is usually provided once the order ships.' },
        { q: 'What affects delivery time?', a: 'Processing time, shipping method, destination, and carrier conditions all play a role.' },
      ],
      ctaTitle: 'Know How Your Order Moves',
      ctaDesc: 'Understand the fulfillment process before you order. Browse the catalog or visit the store for details.',
    },
    'usfans-legit': {
      title: 'Is USFans Legit? A Practical Safety and Trust Guide',
      desc: 'A balanced look at whether USFans is legit — what to check before buying, how to spot red flags, and how to shop safely.',
      category: 'Trust & Safety',
      intro: [
        'Before shopping on any website, it is natural to ask whether it is legitimate and safe to use. Taking a few minutes to verify a store can protect both your money and your personal information.',
        'This guide offers practical steps to evaluate trust, identify warning signs, and make safer decisions without relying on unsupported claims in either direction.',
      ],
      sections: [
        {
          heading: 'What to Check Before Buying',
          paragraphs: [
            'Look for clear contact information, transparent policies, secure payment methods, and realistic product listings. Legitimate stores generally make these details easy to find.',
          ],
        },
        {
          heading: 'Common Warning Signs',
          paragraphs: [
            'Be cautious of prices that seem too good to be true, missing or vague policies, requests for unusual payment methods, and pressure to act immediately.',
          ],
        },
        {
          heading: 'How to Shop Safely',
          paragraphs: [
            'Use secure payment methods, keep your passwords private, avoid suspicious links, and verify important details directly on the store before purchasing.',
          ],
        },
      ],
      faq: [
        { q: 'Is USFans a legitimate website?', a: 'We do not make unsupported claims either way. Instead, we recommend verifying contact details, policies, and secure payment options yourself.' },
        { q: 'What are common red flags?', a: 'Unrealistic prices, missing policies, unusual payment requests, and high-pressure tactics.' },
        { q: 'How can I shop more safely?', a: 'Use secure payments, keep passwords private, and verify details on the store before buying.' },
      ],
      ctaTitle: 'Shop With Confidence',
      ctaDesc: 'Use practical checks before you buy. Browse the catalog and verify details directly on the store.',
    },
    'usfans-review': {
      title: 'USFans Review: Features, Catalog and What to Expect',
      desc: 'An honest overview of the USFans experience — the catalog, categories, and what shoppers should know before ordering.',
      category: 'Review',
      intro: [
        'A good review goes beyond a simple rating and helps you understand what a store actually offers, how it is organized, and what the experience is like. That is exactly what this overview aims to provide.',
        'We walk through the catalog structure, the main product categories, and the practical points to keep in mind so you can decide whether it is the right fit for your needs.',
      ],
      sections: [
        {
          heading: 'The Catalog at a Glance',
          paragraphs: [
            'The store covers a broad range of fashion and fan merchandise, organized into clear categories such as shoes, hoodies, t-shirts, jerseys, accessories, bags, and more.',
          ],
        },
        {
          heading: 'What the Organization Is Like',
          paragraphs: [
            'Instead of a single endless list, products are grouped by category, which makes it easier to find a specific type of item without scrolling through unrelated products.',
          ],
        },
        {
          heading: 'Points to Keep in Mind',
          paragraphs: [
            'As with any store, it is worth reviewing product details, shipping information, and return policies before ordering to ensure the experience matches your expectations.',
          ],
        },
      ],
      faq: [
        { q: 'What does the USFans catalog include?', a: 'It includes shoes, hoodies, t-shirts, jackets, jerseys, accessories, bags, belts, perfumes, and more.' },
        { q: 'How is the catalog organized?', a: 'Products are grouped into clear categories, making it easier to find specific items.' },
        { q: 'What should I check before ordering?', a: 'Review product details, shipping, and return policies before completing your purchase.' },
      ],
      ctaTitle: 'See the Catalog for Yourself',
      ctaDesc: 'Explore the categories to understand what is available, or visit the store to browse the full selection.',
    },
    'usfans-creator-list': {
      title: 'USFans Creator List: How to Organize and Browse Creators',
      desc: 'A guide to understanding USFans creator lists — how they are organized, how to browse them, and how to evaluate the information.',
      category: 'Creator Guide',
      intro: [
        'Creator lists help people discover and organize information about creators in one place. Knowing how these lists are structured makes it much easier to find what you are looking for.',
        'This guide explains what a creator list is, how to browse it effectively, and how to evaluate the information you find rather than taking every entry at face value.',
      ],
      sections: [
        {
          heading: 'What Is a Creator List?',
          paragraphs: [
            'A creator list is a structured collection of creator-related information, typically organized for easier browsing and comparison across multiple entries.',
          ],
        },
        {
          heading: 'How to Browse a Creator List',
          paragraphs: [
            'Start by identifying the type of creator you are interested in, then use categories or filters to narrow the list. Comparing a few entries side by side usually leads to better results.',
          ],
        },
        {
          heading: 'How to Evaluate the Information',
          paragraphs: [
            'Treat list entries as a starting point rather than a final answer. Cross-check important details and be cautious with information that seems incomplete or unverified.',
          ],
        },
      ],
      faq: [
        { q: 'What is a creator list?', a: 'It is a structured collection of creator-related information, organized for easier browsing and comparison.' },
        { q: 'How do I browse a creator list efficiently?', a: 'Identify what you are looking for, then use categories or filters to narrow the results.' },
        { q: 'Should I trust every entry?', a: 'Treat entries as a starting point and cross-check important details independently.' },
      ],
      ctaTitle: 'Browse Creators With Ease',
      ctaDesc: 'Understand how creator lists work, then explore the resources available through the main catalog.',
    },
    'usfans-spreadsheet-search': {
      title: 'USFans Spreadsheet Search: How to Find Items Faster',
      desc: 'Practical tips for searching the USFans Spreadsheet — how to find the right item quickly and use categories effectively.',
      category: 'Search Guide',
      intro: [
        'Finding a specific item in a large catalog is much faster when you know how to search effectively. A few simple techniques can save you time and reduce frustration.',
        'This guide shares practical search tips, explains how to use categories to narrow your results, and highlights common search mistakes to avoid.',
      ],
      sections: [
        {
          heading: 'Start With the Right Category',
          paragraphs: [
            'The fastest way to find something is to jump to the correct category first, then refine from there. This avoids sifting through unrelated products.',
          ],
        },
        {
          heading: 'Use Specific Search Terms',
          paragraphs: [
            'Be specific about what you want — for example, the type of item, style, or color. Specific terms return more relevant results than vague ones.',
          ],
        },
        {
          heading: 'Common Search Mistakes',
          paragraphs: [
            'Using overly broad terms, ignoring category filters, and giving up too quickly are the most common mistakes. A small adjustment often makes a big difference.',
          ],
        },
      ],
      faq: [
        { q: 'How do I find an item faster?', a: 'Jump to the correct category first, then refine with specific search terms.' },
        { q: 'Why are my searches too broad?', a: 'Vague terms return too many results; being specific about type, style, or color helps.' },
        { q: 'What is the quickest way to narrow results?', a: 'Use category filters to remove unrelated products before searching.' },
      ],
      ctaTitle: 'Find What You Need Faster',
      ctaDesc: 'Use better search habits to save time. Explore the categories or visit the store to start browsing.',
    },
    'usfans-safety-guide': {
      title: 'USFans Safety Guide: How to Shop Online Safely',
      desc: 'A practical safety guide for online shopping — protecting your data, spotting suspicious links, and avoiding common risks.',
      category: 'Safety Guide',
      intro: [
        'Online shopping is convenient, but it is important to stay aware of potential risks. A few simple habits can protect your money, your accounts, and your personal information.',
        'This guide covers practical safety practices, how to recognize suspicious activity, and the steps to take if something feels off during your shopping experience.',
      ],
      sections: [
        {
          heading: 'Protect Your Personal Data',
          paragraphs: [
            'Never share passwords or sensitive information with unknown websites. Use strong, unique passwords and enable extra security where available.',
          ],
        },
        {
          heading: 'Spot Suspicious Links',
          paragraphs: [
            'Verify links before clicking, avoid unexpected downloads, and be wary of messages that pressure you to act quickly or request unusual information.',
          ],
        },
        {
          heading: 'What to Do if Something Feels Off',
          paragraphs: [
            'Stop and verify before proceeding. Check the website address, avoid entering payment details on unfamiliar pages, and contact the store directly if you have doubts.',
          ],
        },
      ],
      faq: [
        { q: 'How can I protect my data when shopping?', a: 'Use strong unique passwords, never share them, and avoid entering sensitive details on unfamiliar pages.' },
        { q: 'How do I spot a suspicious link?', a: 'Verify links before clicking and be cautious of unexpected downloads or high-pressure messages.' },
        { q: 'What should I do if something seems wrong?', a: 'Stop, verify the website, and contact the store directly before sharing more information.' },
      ],
      ctaTitle: 'Stay Safe While You Shop',
      ctaDesc: 'Keep these safety habits in mind. Explore the catalog and shop with confidence.',
    },
    'usfans-comparison-guide': {
      title: 'USFans Spreadsheet vs Other Discovery Methods: A Comparison',
      desc: 'A clear comparison of the USFans Spreadsheet approach versus manual search, directories, and unstructured lists.',
      category: 'Comparison',
      intro: [
        'There are many ways to browse and discover products, and they are not all equally efficient. Understanding the differences can help you choose the approach that best fits your needs.',
        'This guide compares organized spreadsheet-style browsing against other discovery methods, looking at organization, searchability, and ease of comparison.',
      ],
      sections: [
        {
          heading: 'Organized vs. Unorganized Browsing',
          paragraphs: [
            'Organized browsing groups products into clear categories, while unorganized browsing requires you to sift through everything manually. The difference in efficiency is significant.',
          ],
        },
        {
          heading: 'Searchability and Comparison',
          paragraphs: [
            'Structured catalogs make it easier to search within a category and compare similar items side by side, which generally leads to better decisions.',
          ],
        },
        {
          heading: 'Which Method Should You Use?',
          paragraphs: [
            'For quick, one-off lookups, manual search can work. For focused research and comparison, an organized approach is usually faster and less frustrating.',
          ],
        },
      ],
      faq: [
        { q: 'What is the difference between organized and unorganized browsing?', a: 'Organized browsing groups products into categories, while unorganized browsing requires manual sifting.' },
        { q: 'Which method is faster?', a: 'Organized browsing is usually faster for focused research and comparison.' },
        { q: 'When is manual search useful?', a: 'Manual search can work for quick, one-off lookups.' },
      ],
      ctaTitle: 'Choose the Right Approach',
      ctaDesc: 'Understand the differences, then explore the organized catalog to find what you need.',
    },
  },

  pl: {
    'usfans-spreadsheet-guide': {
      title: 'Przewodnik USFans Spreadsheet: porządkuj i kupuj cały katalog',
      desc: 'Kompletny przewodnik po USFans Spreadsheet — jak porządkuje kategorie mody, jak sprawnie przeglądać i porady zakupowe.',
      category: 'Kompletny przewodnik',
      intro: [
        'Zakupy w dużym katalogu mody mogą przytłaczać, zwłaszcza gdy produkty są rozrzucone na dziesiątkach stron. USFans Spreadsheet powstał właśnie po to, by rozwiązać ten problem: łączy buty, bluzy, koszulki, kurtki, stroje sportowe i akcesoria w jeden uporządkowany przegląd.',
        'W tym przewodniku wyjaśniamy, czym jest USFans Spreadsheet, jak działa jego system kategorii i jak szybciej znaleźć odpowiedni produkt.',
      ],
      sections: [
        {
          heading: 'Czym jest USFans Spreadsheet?',
          paragraphs: [
            'To uporządkowany katalog, który grupuje szeroki wybór mody i gadżetów fanowskich w jasne, logiczne kategorie.',
          ],
          bullets: [
            'Buty — sneakersy i obuwie codzienne',
            'Bluzy/Swetry — wygodne bluzy i dzianina',
            'Stroje sportowe — koszulki drużynowe',
            'Akcesoria — drobiazgi dopełniające stylizację',
          ],
        },
        {
          heading: 'Jak sprawnie przeglądać',
          paragraphs: [
            'Zacznij od określenia, czego naprawdę potrzebujesz. Użyj listy rozwijanej lub przycisków kategorii, aby od razu przejść do właściwej sekcji.',
          ],
        },
        {
          heading: 'Porady zakupowe',
          paragraphs: [
            'Sprawdź szczegóły, rozmiary i dostępność przed zamówieniem. Zapoznaj się z informacjami o wysyłce i zwrotach oraz dbaj o poufność danych płatności.',
          ],
        },
      ],
      faq: [
        { q: 'Czym jest USFans Spreadsheet?', a: 'To uporządkowany katalog, który grupuje modę i gadżety w jasne kategorie, ułatwiając szybkie znalezienie produktu.' },
        { q: 'Jak szybko znaleźć konkretny produkt?', a: 'Użyj listy rozwijanej lub przycisków kategorii na stronie głównej, aby przejść do właściwej sekcji.' },
        { q: 'Czy USFans Spreadsheet to sklep?', a: 'To zasób informacyjny i nawigacyjny. Zakupy realizowane są w sklepie, do którego prowadzi.' },
      ],
      ctaTitle: 'Gotowy na przegląd całego katalogu?',
      ctaDesc: 'Teraz, gdy wiesz, jak działa USFans Spreadsheet, przeglądaj kategorie lub odwiedź sklep, aby zacząć zakupy.',
    },
    'usfans-shipping-cost': {
      title: 'Koszt wysyłki USFans: co warto wiedzieć przed zamówieniem',
      desc: 'Praktyczny przewodnik po kosztach wysyłki USFans — jak działa, co wpływa na cenę i jak uniknąć niespodzianek.',
      category: 'Przewodnik wysyłki',
      intro: [
        'Koszt wysyłki to jedno z najczęstszych pytań przed złożeniem zamówienia, ponieważ bezpośrednio wpływa na ostateczną cenę. Zrozumienie zasad pomaga zaplanować budżet.',
        'Ten przewodnik wyjaśnia, co wpływa na koszty wysyłki, jak różnią się metody dostawy i jakie kroki pomogą utrzymać koszty pod kontrolą.',
      ],
      sections: [
        { heading: 'Co wpływa na koszt wysyłki?', paragraphs: ['Kraj docelowy, waga i rozmiar paczki, wybrana metoda dostawy oraz aktualne promocje to główne czynniki.'] },
        { heading: 'Metody wysyłki', paragraphs: ['Sklepy oferują różne metody — od ekonomicznej po ekspresową. Szybsze są droższe, ale docierają wcześniej.'] },
        { heading: 'Jak obniżyć koszty', paragraphs: ['Łącz produkty w jedno zamówienie, śledź progi darmowej wysyłki i porównuj opcje przy kasie.'] },
      ],
      faq: [
        { q: 'Jak liczony jest koszt wysyłki?', a: 'Zależy od kraju, wagi i rozmiaru paczki, metody dostawy oraz promocji.' },
        { q: 'Czy mogę obniżyć koszt?', a: 'Tak — łącz produkty, szukaj progów darmowej wysyłki i porównuj metody.' },
        { q: 'Gdzie znajdę dokładną cenę?', a: 'Dokładna cena pojawia się przy kasie, na podstawie adresu i wybranej metody.' },
      ],
      ctaTitle: 'Planuj zamówienie z pewnością',
      ctaDesc: 'Poznaj opcje wysyłki przed zakupem. Przeglądaj katalog lub odwiedź sklep, aby sprawdzić szczegóły.',
    },
    'usfans-coupon': {
      title: 'Przewodnik po kuponach USFans: jak działają rabaty',
      desc: 'Dowiedz się, jak działają kupony i rabaty USFans, gdzie szukać oszczędności i jak je poprawnie zastosować.',
      category: 'Przewodnik oszczędności',
      intro: [
        'Kupony to popularny sposób oszczędzania, ale działają tylko wtedy, gdy wiesz, gdzie ich szukać i jak ich używać. Zrozumienie zasad może przynieść realne oszczędności.',
        'Ten przewodnik wyjaśnia, jak działają kupony, gdzie szukać ważnych rabatów i jakich błędów unikać.',
      ],
      sections: [
        { heading: 'Jak działają kupony', paragraphs: ['Kupon to kod wpisywany przy kasie, który daje rabat, np. procentowy lub kwotowy.'] },
        { heading: 'Gdzie szukać rabatów', paragraphs: ['Oszczędności są udostępniane przez oficjalne ogłoszenia, newslettery i promocje sezonowe.'] },
        { heading: 'Typowe błędy', paragraphs: ['Wpisywanie przeterminowanego kodu, stosowanie do wykluczonych produktów lub pomijanie minimalnej kwoty zakupu.'] },
      ],
      faq: [
        { q: 'Jak zastosować kupon?', a: 'Wpisz kod w odpowiednim polu przy kasie, a rabat zostanie naliczony przed finalizacją.' },
        { q: 'Gdzie szukać aktualnych kuponów?', a: 'W oficjalnych ogłoszeniach, newsletterach i promocjach sezonowych.' },
        { q: 'Dlaczego kupon nie zadziałał?', a: 'Może być przeterminowany, dotyczyć tylko niektórych produktów lub wymagać minimalnej kwoty.' },
      ],
      ctaTitle: 'Wykorzystaj swoje oszczędności',
      ctaDesc: 'Poznaj zasady rabatów przed zakupami. Przeglądaj katalog i szukaj ważnych oszczędności.',
    },
    'usfans-discount-code': {
      title: 'Kody rabatowe USFans: oszczędzaj więcej przy kasie',
      desc: 'Jasny przewodnik po kodach rabatowych USFans — czym są, czym różnią się od kuponów i jak ich poprawnie używać.',
      category: 'Przewodnik oszczędności',
      intro: [
        'Kody rabatowe to prosty, ale skuteczny sposób na obniżenie kosztów zamówienia. Zrozumienie różnicy między kodem a kuponem pomaga wybrać najlepszą opcję.',
        'Ten przewodnik omawia, czym są kody rabatowe, jak je poprawnie wpisywać i co zrobić, gdy kod nie działa.',
      ],
      sections: [
        { heading: 'Czym jest kod rabatowy?', paragraphs: ['To krótki tekst odblokowujący konkretny rabat, np. procent lub darmową wysyłkę.'] },
        { heading: 'Kod a kupon', paragraphs: ['Pojęcia są często używane zamiennie, ale kod odnosi się do samego ciągu znaków.'] },
        { heading: 'Jak go poprawnie zastosować', paragraphs: ['Skopiuj dokładnie kod, wpisz w odpowiednim polu i potwierdź rabat przed płatnością.'] },
      ],
      faq: [
        { q: 'Czym jest kod rabatowy?', a: 'To kod tekstowy odblokowujący rabat przy kasie.' },
        { q: 'Czym różni się od kuponu?', a: 'Kod odnosi się do samego ciągu znaków, kupon to szersza promocja.' },
        { q: 'Co jeśli kod nie działa?', a: 'Sprawdź literówki, ważność i czy kod dotyczy produktów w koszyku.' },
      ],
      ctaTitle: 'Odblokuj swoje oszczędności',
      ctaDesc: 'Zrozum kody rabatowe przed zamówieniem. Przeglądaj katalog i stosuj ważne kody przy kasie.',
    },
    'usfans-warehouse': {
      title: 'Magazyn USFans: jak przechowywane i wysyłane są zamówienia',
      desc: 'Dowiedz się, jak działa magazyn i realizacja zamówień USFans oraz co to oznacza dla czasu dostawy.',
      category: 'Przewodnik wysyłki',
      intro: [
        'Magazyn to miejsce, w którym produkty są przechowywane przed zapakowaniem i wysyłką. Zrozumienie tego procesu pomaga realnie ocenić czas dostawy.',
        'Ten przewodnik wyjaśnia, co robi magazyn, jak zamówienia trafiają od przechowywania do Twoich drzwi i co wpływa na czas realizacji.',
      ],
      sections: [
        { heading: 'Co robi magazyn?', paragraphs: ['Przechowuje, porządkuje i przygotowuje zapasy do wysyłki.'] },
        { heading: 'Jak realizowane są zamówienia', paragraphs: ['Po kasie zamówienie jest kompletowane i pakowane, a następnie wysyłane z numerem śledzenia.'] },
        { heading: 'Co wpływa na czas dostawy', paragraphs: ['Czas realizacji, metoda wysyłki, odległość i warunki przewoźnika.'] },
      ],
      faq: [
        { q: 'Czym jest magazyn?', a: 'To miejsce, w którym produkty są przechowywane, pakowane i przygotowywane do wysyłki.' },
        { q: 'Ile trwa realizacja zamówienia?', a: 'Czas zależy od sklepu i liczby zamówień; numer śledzenia pojawia się po wysyłce.' },
        { q: 'Co wpływa na czas dostawy?', a: 'Czas realizacji, metoda wysyłki, odległość i warunki przewoźnika.' },
      ],
      ctaTitle: 'Wiedz, jak porusza się Twoje zamówienie',
      ctaDesc: 'Poznaj proces realizacji przed zamówieniem. Przeglądaj katalog lub odwiedź sklep.',
    },
    'usfans-legit': {
      title: 'Czy USFans jest legalny? Praktyczny przewodnik bezpieczeństwa',
      desc: 'Zrównoważone spojrzenie na wiarygodność USFans — co sprawdzić, jak rozpoznać sygnały ostrzegawcze i jak bezpiecznie kupować.',
      category: 'Zaufanie i bezpieczeństwo',
      intro: [
        'Przed zakupami w jakimkolwiek sklepie warto sprawdzić, czy jest legalny i bezpieczny. Kilka minut weryfikacji chroni pieniądze i dane osobowe.',
        'Ten przewodnik oferuje praktyczne kroki oceny zaufania, rozpoznawania ostrzeżeń i bezpieczniejszych decyzji bez niepopartych twierdzeń.',
      ],
      sections: [
        { heading: 'Co sprawdzić przed zakupem', paragraphs: ['Wyraźny kontakt, przejrzyste zasady, bezpieczne płatności i realistyczne opisy produktów.'] },
        { heading: 'Sygnały ostrzegawcze', paragraphs: ['Zbyt niskie ceny, brak lub niejasne zasady, nietypowe metody płatności i presja czasu.'] },
        { heading: 'Jak bezpiecznie kupować', paragraphs: ['Bezpieczne płatności, poufne hasła, unikanie podejrzanych linków i weryfikacja szczegółów w sklepie.'] },
      ],
      faq: [
        { q: 'Czy USFans to legalna strona?', a: 'Nie formułujemy niepopartych twierdzeń. Zalecamy samodzielną weryfikację kontaktu, zasad i płatności.' },
        { q: 'Jakie są sygnały ostrzegawcze?', a: 'Nierealne ceny, brak zasad, nietypowe płatności i presja czasu.' },
        { q: 'Jak kupować bezpieczniej?', a: 'Bezpieczne płatności, poufne hasła i weryfikacja szczegółów przed zakupem.' },
      ],
      ctaTitle: 'Kupuj z pewnością',
      ctaDesc: 'Stosuj praktyczne kontrole przed zakupem. Przeglądaj katalog i weryfikuj szczegóły w sklepie.',
    },
    'usfans-review': {
      title: 'Recenzja USFans: funkcje, katalog i czego się spodziewać',
      desc: 'Szczery przegląd doświadczenia USFans — katalog, kategorie i co warto wiedzieć przed zamówieniem.',
      category: 'Recenzja',
      intro: [
        'Dobra recenzja wykracza poza ocenę i pomaga zrozumieć, co oferuje sklep, jak jest zorganizowany i jakie jest doświadczenie. To właśnie zapewnia ten przegląd.',
        'Omawiamy strukturę katalogu, główne kategorie produktów i praktyczne kwestie, abyś mógł zdecydować, czy to odpowiedni wybór.',
      ],
      sections: [
        { heading: 'Katalog w skrócie', paragraphs: ['Sklep obejmuje szeroki wybór mody i gadżetów, w kategorie takie jak buty, bluzy, koszulki, stroje i akcesoria.'] },
        { heading: 'Jak wygląda organizacja', paragraphs: ['Produkty są pogrupowane według kategorii, co ułatwia znalezienie konkretnego typu produktu.'] },
        { heading: 'O czym pamiętać', paragraphs: ['Warto sprawdzić szczegóły produktów, informacje o wysyłce i zasady zwrotów przed zamówieniem.'] },
      ],
      faq: [
        { q: 'Co zawiera katalog USFans?', a: 'Buty, bluzy, koszulki, kurtki, stroje, akcesoria, torby, paski, perfumy i więcej.' },
        { q: 'Jak zorganizowany jest katalog?', a: 'Produkty są pogrupowane w jasne kategorie.' },
        { q: 'Co sprawdzić przed zamówieniem?', a: 'Szczegóły produktów, wysyłkę i zasady zwrotów.' },
      ],
      ctaTitle: 'Zobacz katalog samodzielnie',
      ctaDesc: 'Przeglądaj kategorie, aby zrozumieć ofertę, lub odwiedź sklep, aby zobaczyć pełny wybór.',
    },
    'usfans-creator-list': {
      title: 'Lista twórców USFans: jak porządkować i przeglądać twórców',
      desc: 'Przewodnik po listach twórców USFans — jak są zorganizowane, jak je przeglądać i jak oceniać informacje.',
      category: 'Przewodnik twórców',
      intro: [
        'Listy twórców pomagają odkrywać i porządkować informacje w jednym miejscu. Znajomość struktury ułatwia znalezienie tego, czego szukasz.',
        'Ten przewodnik wyjaśnia, czym jest lista twórców, jak ją efektywnie przeglądać i jak oceniać znalezione informacje.',
      ],
      sections: [
        { heading: 'Czym jest lista twórców?', paragraphs: ['To uporządkowany zbiór informacji o twórcach, ułatwiający przeglądanie i porównywanie.'] },
        { heading: 'Jak przeglądać listę', paragraphs: ['Określ typ twórcy, a następnie użyj kategorii lub filtrów, aby zawęzić wyniki.'] },
        { heading: 'Jak oceniać informacje', paragraphs: ['Traktuj wpisy jako punkt wyjścia i weryfikuj ważne szczegóły niezależnie.'] },
      ],
      faq: [
        { q: 'Czym jest lista twórców?', a: 'To uporządkowany zbiór informacji o twórcach do przeglądania i porównywania.' },
        { q: 'Jak efektywnie przeglądać listę?', a: 'Określ, czego szukasz, i użyj kategorii lub filtrów.' },
        { q: 'Czy ufać każdemu wpisowi?', a: 'Traktuj wpisy jako punkt wyjścia i weryfikuj szczegóły niezależnie.' },
      ],
      ctaTitle: 'Przeglądaj twórców z łatwością',
      ctaDesc: 'Zrozum, jak działają listy twórców, i korzystaj z zasobów dostępnych w katalogu.',
    },
    'usfans-spreadsheet-search': {
      title: 'Wyszukiwanie USFans Spreadsheet: jak szybciej znaleźć produkty',
      desc: 'Praktyczne porady dotyczące wyszukiwania w USFans Spreadsheet — jak szybko znaleźć produkt i skutecznie używać kategorii.',
      category: 'Przewodnik wyszukiwania',
      intro: [
        'Znalezienie konkretnego produktu w dużym katalogu jest szybsze, gdy wiesz, jak efektywnie szukać. Kilka technik oszczędza czas.',
        'Ten przewodnik dzieli się poradami, wyjaśnia, jak używać kategorii, i wskazuje częste błędy wyszukiwania.',
      ],
      sections: [
        { heading: 'Zacznij od właściwej kategorii', paragraphs: ['Najszybciej przejść do właściwej kategorii, a potem zawęzić wyniki.'] },
        { heading: 'Używaj konkretnych fraz', paragraphs: ['Bądź konkretny — typ, styl lub kolor. Konkretne frazy dają trafniejsze wyniki.'] },
        { heading: 'Częste błędy', paragraphs: ['Zbyt ogólne frazy, ignorowanie filtrów i zbyt szybka rezygnacja.'] },
      ],
      faq: [
        { q: 'Jak szybciej znaleźć produkt?', a: 'Przejdź do właściwej kategorii, a potem zawęź konkretnymi frazami.' },
        { q: 'Dlaczego wyniki są zbyt ogólne?', a: 'Ogólne frazy dają zbyt wiele wyników; konkretność pomaga.' },
        { q: 'Jak najszybciej zawęzić wyniki?', a: 'Użyj filtrów kategorii, aby usunąć niepowiązane produkty.' },
      ],
      ctaTitle: 'Znajdź to, czego potrzebujesz, szybciej',
      ctaDesc: 'Korzystaj z lepszych nawyków wyszukiwania. Przeglądaj kategorie lub odwiedź sklep.',
    },
    'usfans-safety-guide': {
      title: 'Przewodnik bezpieczeństwa USFans: jak bezpiecznie kupować online',
      desc: 'Praktyczny przewodnik bezpieczeństwa — ochrona danych, rozpoznawanie linków i unikanie zagrożeń.',
      category: 'Przewodnik bezpieczeństwa',
      intro: [
        'Zakupy online są wygodne, ale warto być świadomym zagrożeń. Kilka nawyków chroni pieniądze, konta i dane osobowe.',
        'Ten przewodnik omawia praktyki bezpieczeństwa, rozpoznawanie podejrzanych działań i kroki w razie wątpliwości.',
      ],
      sections: [
        { heading: 'Chroń dane osobowe', paragraphs: ['Nigdy nie udostępniaj haseł ani danych wrażliwych nieznanym stronom. Używaj silnych, unikalnych haseł.'] },
        { heading: 'Rozpoznawaj podejrzane linki', paragraphs: ['Weryfikuj linki, unikaj pobrań i uważaj na presję czasu.'] },
        { heading: 'Co zrobić w razie wątpliwości', paragraphs: ['Zatrzymaj się, zweryfikuj adres i skontaktuj się ze sklepem przed podaniem danych.'] },
      ],
      faq: [
        { q: 'Jak chronić dane podczas zakupów?', a: 'Używaj silnych, unikalnych haseł i nie podawaj danych na nieznanych stronach.' },
        { q: 'Jak rozpoznać podejrzany link?', a: 'Weryfikuj linki i uważaj na nieoczekiwane pobrania lub presję.' },
        { q: 'Co zrobić, gdy coś jest nie tak?', a: 'Zatrzymaj się, zweryfikuj stronę i skontaktuj się ze sklepem.' },
      ],
      ctaTitle: 'Bądź bezpieczny podczas zakupów',
      ctaDesc: 'Pamiętaj o tych nawykach. Przeglądaj katalog i kupuj z pewnością.',
    },
    'usfans-comparison-guide': {
      title: 'USFans Spreadsheet a inne metody: porównanie',
      desc: 'Jasne porównanie podejścia USFans Spreadsheet z wyszukiwaniem ręcznym, katalogami i listami.',
      category: 'Porównanie',
      intro: [
        'Istnieje wiele sposobów przeglądania produktów i nie wszystkie są równie wydajne. Zrozumienie różnic pomaga wybrać najlepsze podejście.',
        'Ten przewodnik porównuje zorganizowane przeglądanie z innymi metodami pod kątem organizacji i łatwości porównywania.',
      ],
      sections: [
        { heading: 'Zorganizowane a niezorganizowane', paragraphs: ['Zorganizowane grupowanie ułatwia nawigację, a niezorganizowane wymaga ręcznego przeszukiwania.'] },
        { heading: 'Wyszukiwanie i porównywanie', paragraphs: ['Katalogi strukturalne ułatwiają wyszukiwanie i porównywanie podobnych produktów.'] },
        { heading: 'Którą metodę wybrać?', paragraphs: ['Do szybkich wyszukań sprawdzi się ręczne, do badań i porównań — zorganizowane.'] },
      ],
      faq: [
        { q: 'Jaka jest różnica między zorganizowanym a niezorganizowanym?', a: 'Zorganizowane grupuje produkty, niezorganizowane wymaga ręcznego szukania.' },
        { q: 'Która metoda jest szybsza?', a: 'Zorganizowana jest zwykle szybsza do badań i porównań.' },
        { q: 'Kiedy przydaje się ręczne wyszukiwanie?', a: 'Do szybkich, jednorazowych wyszukań.' },
      ],
      ctaTitle: 'Wybierz właściwe podejście',
      ctaDesc: 'Zrozum różnice i przeglądaj zorganizowany katalog, aby znaleźć to, czego potrzebujesz.',
    },
  },

  es: {
    'usfans-spreadsheet-guide': {
      title: 'Guía de USFans Spreadsheet: organiza y compra todo el catálogo',
      desc: 'Una guía completa de USFans Spreadsheet: cómo organiza las categorías de moda, cómo navegar y consejos de compra.',
      category: 'Guía completa',
      intro: [
        'Comprar en un catálogo de moda grande puede resultar abrumador. USFans Spreadsheet nació para resolverlo: reúne zapatos, sudaderas, camisetas, chaquetas, jerseys y accesorios en un resumen organizado.',
        'En esta guía explicamos qué es USFans Spreadsheet, cómo funciona su sistema de categorías y cómo encontrar el artículo adecuado más rápido.',
      ],
      sections: [
        {
          heading: '¿Qué es USFans Spreadsheet?',
          paragraphs: [
            'Es un catálogo estructurado que agrupa una amplia gama de moda y productos para fans en categorías claras y lógicas.',
          ],
          bullets: [
            'Zapatos: zapatillas y calzado casual',
            'Sudaderas/Jerséis: prendas cómodas y de punto',
            'Jerseys: camisetas deportivas y de equipo',
            'Accesorios: detalles que completan un conjunto',
          ],
        },
        { heading: 'Cómo navegar con eficacia', paragraphs: ['Empieza identificando lo que necesitas. Usa el menú desplegable o los botones de categoría para ir directo a la sección deseada.'] },
        { heading: 'Consejos de compra', paragraphs: ['Verifica detalles, tallas y disponibilidad antes de pedir. Revisa la información de envío y devoluciones y protege tus datos de pago.'] },
      ],
      faq: [
        { q: '¿Qué es USFans Spreadsheet?', a: 'Un catálogo organizado que agrupa moda y productos en categorías claras para encontrar lo que necesitas más rápido.' },
        { q: '¿Cómo encuentro un producto rápido?', a: 'Usa el menú desplegable o los botones de categoría en la página principal.' },
        { q: '¿Es una tienda?', a: 'Es un recurso informativo y de navegación. Las compras se completan en la tienda a la que enlaza.' },
      ],
      ctaTitle: '¿Listo para explorar el catálogo?',
      ctaDesc: 'Ahora que entiendes cómo funciona, explora las categorías o visita la tienda para empezar a comprar.',
    },
    'usfans-shipping-cost': {
      title: 'Coste de envío de USFans: lo que debes saber antes de pedir',
      desc: 'Una guía práctica sobre el coste de envío de USFans: cómo funciona, qué lo afecta y cómo evitar sorpresas.',
      category: 'Guía de envío',
      intro: [
        'El coste de envío es una de las preguntas más comunes antes de pedir, porque afecta al precio final. Entenderlo ayuda a planificar tu presupuesto.',
        'Esta guía explica los factores que influyen en el envío, las diferencias entre métodos y cómo mantener los costes bajo control.',
      ],
      sections: [
        { heading: 'Qué afecta al coste de envío', paragraphs: ['El país de destino, el peso y tamaño del paquete, el método elegido y las promociones actuales.'] },
        { heading: 'Métodos de envío', paragraphs: ['Hay métodos desde estándar hasta exprés. Los más rápidos suelen costar más, pero llegan antes.'] },
        { heading: 'Cómo mantener los costes bajos', paragraphs: ['Agrupa productos, busca umbrales de envío gratis y compara opciones al pagar.'] },
      ],
      faq: [
        { q: '¿Cómo se calcula el envío?', a: 'Depende del destino, peso, tamaño, método y promociones.' },
        { q: '¿Puedo reducir el coste?', a: 'Sí: agrupa productos y busca envío gratis.' },
        { q: '¿Dónde veo el precio exacto?', a: 'En el pago, según tu dirección y método.' },
      ],
      ctaTitle: 'Planifica tu pedido con confianza',
      ctaDesc: 'Conoce tus opciones de envío antes de comprar. Explora el catálogo o visita la tienda.',
    },
    'usfans-coupon': {
      title: 'Guía de cupones de USFans: cómo funcionan los descuentos',
      desc: 'Aprende cómo funcionan los cupones y descuentos de USFans, dónde buscar ahorros y cómo aplicarlos.',
      category: 'Guía de ahorro',
      intro: [
        'Los cupones son una forma popular de ahorrar, pero solo ayudan si sabes dónde encontrarlos y cómo usarlos.',
        'Esta guía explica cómo funcionan los cupones, dónde buscar ahorros válidos y los errores comunes a evitar.',
      ],
      sections: [
        { heading: 'Cómo funcionan los cupones', paragraphs: ['Un cupón es un código que se introduce al pagar para obtener un descuento.'] },
        { heading: 'Dónde encontrar ahorros', paragraphs: ['En anuncios oficiales, boletines y promociones de temporada.'] },
        { heading: 'Errores comunes', paragraphs: ['Introducir códigos caducados o usarlos en productos excluidos.'] },
      ],
      faq: [
        { q: '¿Cómo aplico un cupón?', a: 'Introduce el código en el campo correspondiente al pagar.' },
        { q: '¿Dónde encuentro cupones actuales?', a: 'En anuncios oficiales, boletines y promociones.' },
        { q: '¿Por qué no funcionó mi cupón?', a: 'Puede estar caducado o tener condiciones específicas.' },
      ],
      ctaTitle: 'Aprovecha tus ahorros',
      ctaDesc: 'Conoce cómo funcionan los descuentos antes de comprar.',
    },
    'usfans-discount-code': {
      title: 'Códigos de descuento de USFans: ahorra más al pagar',
      desc: 'Una guía clara de códigos de descuento de USFans: qué son, en qué se diferencian de los cupones y cómo usarlos.',
      category: 'Guía de ahorro',
      intro: [
        'Los códigos de descuento son una forma sencilla de reducir el coste de tu pedido.',
        'Esta guía explica qué son, cómo introducirlos correctamente y qué hacer si un código no funciona.',
      ],
      sections: [
        { heading: 'Qué es un código de descuento', paragraphs: ['Un texto corto que desbloquea un descuento al pagar.'] },
        { heading: 'Código vs. cupón', paragraphs: ['Suelen usarse indistintamente, pero el código se refiere al propio texto.'] },
        { heading: 'Cómo aplicarlo', paragraphs: ['Copia el código, introdúcelo y confirma el descuento antes de pagar.'] },
      ],
      faq: [
        { q: '¿Qué es un código de descuento?', a: 'Un texto que desbloquea un descuento al pagar.' },
        { q: '¿En qué se diferencia de un cupón?', a: 'El código se refiere al texto; el cupón, a la promoción.' },
        { q: '¿Qué hago si no funciona?', a: 'Revisa erratas, caducidad y condiciones.' },
      ],
      ctaTitle: 'Desbloquea tus ahorros',
      ctaDesc: 'Entiende los códigos antes de pedir.',
    },
    'usfans-warehouse': {
      title: 'Almacén de USFans: cómo se guardan y envían los pedidos',
      desc: 'Aprende cómo funciona el almacén y la logística de USFans y qué significa para los plazos de entrega.',
      category: 'Guía de envío',
      intro: [
        'El almacén es donde se guardan los productos antes de empaquetarse y enviarse. Entender el proceso ayuda a estimar los plazos.',
        'Esta guía explica qué hace un almacén, cómo se procesan los pedidos y qué afecta al tiempo de entrega.',
      ],
      sections: [
        { heading: 'Qué hace un almacén', paragraphs: ['Guarda, organiza y prepara el inventario para el envío.'] },
        { heading: 'Cómo se procesan los pedidos', paragraphs: ['Tras el pago, se recogen y empaquetan los artículos, y luego se envían.'] },
        { heading: 'Qué afecta a la entrega', paragraphs: ['El tiempo de procesado, el método y la distancia.'] },
      ],
      faq: [
        { q: '¿Qué es un almacén?', a: 'El lugar donde se guardan y preparan los productos para el envío.' },
        { q: '¿Cuánto tarda el procesado?', a: 'Varía según la tienda; el seguimiento aparece tras el envío.' },
        { q: '¿Qué afecta a la entrega?', a: 'Procesado, método y distancia.' },
      ],
      ctaTitle: 'Sabe cómo se mueve tu pedido',
      ctaDesc: 'Entiende la logística antes de pedir.',
    },
    'usfans-legit': {
      title: '¿Es legítimo USFans? Guía práctica de seguridad y confianza',
      desc: 'Una mirada equilibrada a si USFans es legítimo: qué comprobar, cómo detectar señales y cómo comprar con seguridad.',
      category: 'Confianza y seguridad',
      intro: [
        'Antes de comprar en cualquier web, es natural preguntarse si es legítima. Verificar protege tu dinero y tus datos.',
        'Esta guía ofrece pasos prácticos para evaluar la confianza y detectar señales de alerta.',
      ],
      sections: [
        { heading: 'Qué comprobar antes de comprar', paragraphs: ['Contacto claro, políticas transparentes y pagos seguros.'] },
        { heading: 'Señales de alerta', paragraphs: ['Precios irreales, políticas ausentes y métodos de pago inusuales.'] },
        { heading: 'Cómo comprar con seguridad', paragraphs: ['Pagos seguros, contraseñas privadas y verificación de detalles.'] },
      ],
      faq: [
        { q: '¿Es legítima la web?', a: 'No hacemos afirmaciones sin respaldo; recomendamos verificar contacto, políticas y pagos.' },
        { q: '¿Cuáles son las señales de alerta?', a: 'Precios irreales y políticas ausentes.' },
        { q: '¿Cómo compro con seguridad?', a: 'Pagos seguros y verificación de detalles.' },
      ],
      ctaTitle: 'Compra con confianza',
      ctaDesc: 'Aplica comprobaciones prácticas antes de comprar.',
    },
    'usfans-review': {
      title: 'Reseña de USFans: funciones, catálogo y qué esperar',
      desc: 'Un resumen honesto de la experiencia de USFans: el catálogo, las categorías y qué saber antes de pedir.',
      category: 'Reseña',
      intro: [
        'Una buena reseña ayuda a entender qué ofrece una tienda y cómo está organizada.',
        'Repasamos la estructura del catálogo y las principales categorías de productos.',
      ],
      sections: [
        { heading: 'El catálogo de un vistazo', paragraphs: ['Cubre una amplia gama de moda, organizada en categorías claras.'] },
        { heading: 'Cómo es la organización', paragraphs: ['Los productos se agrupan por categoría, facilitando la búsqueda.'] },
        { heading: 'Puntos a recordar', paragraphs: ['Revisa detalles, envío y devoluciones antes de pedir.'] },
      ],
      faq: [
        { q: '¿Qué incluye el catálogo?', a: 'Zapatos, sudaderas, camisetas, jerseys, accesorios y más.' },
        { q: '¿Cómo está organizado?', a: 'En categorías claras.' },
        { q: '¿Qué revisar antes de pedir?', a: 'Detalles, envío y devoluciones.' },
      ],
      ctaTitle: 'Mira el catálogo tú mismo',
      ctaDesc: 'Explora las categorías o visita la tienda.',
    },
    'usfans-creator-list': {
      title: 'Lista de creadores de USFans: cómo organizar y navegar',
      desc: 'Una guía de las listas de creadores de USFans: cómo están organizadas y cómo evaluar la información.',
      category: 'Guía de creadores',
      intro: [
        'Las listas de creadores ayudan a descubrir y organizar información en un solo lugar.',
        'Esta guía explica qué es una lista de creadores y cómo evaluar la información.',
      ],
      sections: [
        { heading: 'Qué es una lista de creadores', paragraphs: ['Una colección estructurada de información sobre creadores.'] },
        { heading: 'Cómo navegarla', paragraphs: ['Identifica el tipo de creador y usa categorías o filtros.'] },
        { heading: 'Cómo evaluar la información', paragraphs: ['Trata las entradas como punto de partida y verifica los detalles.'] },
      ],
      faq: [
        { q: '¿Qué es una lista de creadores?', a: 'Una colección estructurada de información sobre creadores.' },
        { q: '¿Cómo navegarla?', a: 'Usa categorías o filtros.' },
        { q: '¿Debo confiar en cada entrada?', a: 'Trátalas como punto de partida y verifica.' },
      ],
      ctaTitle: 'Navega a los creadores con facilidad',
      ctaDesc: 'Entiende cómo funcionan las listas de creadores.',
    },
    'usfans-spreadsheet-search': {
      title: 'Búsqueda en USFans Spreadsheet: cómo encontrar más rápido',
      desc: 'Consejos prácticos para buscar en USFans Spreadsheet y encontrar productos más rápido.',
      category: 'Guía de búsqueda',
      intro: [
        'Encontrar un producto específico es más rápido cuando sabes buscar con eficacia.',
        'Esta guía comparte consejos de búsqueda y errores comunes.',
      ],
      sections: [
        { heading: 'Empieza por la categoría correcta', paragraphs: ['Ve directo a la categoría correcta y refina desde ahí.'] },
        { heading: 'Usa términos específicos', paragraphs: ['Sé específico sobre tipo, estilo o color.'] },
        { heading: 'Errores comunes', paragraphs: ['Términos demasiado amplios y filtros ignorados.'] },
      ],
      faq: [
        { q: '¿Cómo encontrar más rápido?', a: 'Ve a la categoría correcta y usa términos específicos.' },
        { q: '¿Por qué mis búsquedas son amplias?', a: 'Términos vagos devuelven demasiados resultados.' },
        { q: '¿Cómo acotar resultados?', a: 'Usa filtros de categoría.' },
      ],
      ctaTitle: 'Encuentra lo que necesitas más rápido',
      ctaDesc: 'Usa mejores hábitos de búsqueda.',
    },
    'usfans-safety-guide': {
      title: 'Guía de seguridad de USFans: cómo comprar online con seguridad',
      desc: 'Una guía práctica de seguridad para compras online: proteger datos y evitar riesgos.',
      category: 'Guía de seguridad',
      intro: [
        'Comprar online es cómodo, pero conviene estar atento a los riesgos.',
        'Esta guía cubre prácticas de seguridad y cómo reconocer actividades sospechosas.',
      ],
      sections: [
        { heading: 'Protege tus datos', paragraphs: ['Nunca compartas contraseñas ni datos sensibles con webs desconocidas.'] },
        { heading: 'Detecta enlaces sospechosos', paragraphs: ['Verifica los enlaces y evita descargas inesperadas.'] },
        { heading: 'Qué hacer si algo parece raro', paragraphs: ['Detente, verifica la web y contacta con la tienda.'] },
      ],
      faq: [
        { q: '¿Cómo protejo mis datos?', a: 'Usa contraseñas fuertes y únicas.' },
        { q: '¿Cómo detecto un enlace sospechoso?', a: 'Verifica los enlaces antes de hacer clic.' },
        { q: '¿Qué hago si algo parece raro?', a: 'Detente y contacta con la tienda.' },
      ],
      ctaTitle: 'Mantente seguro al comprar',
      ctaDesc: 'Ten en cuenta estos hábitos de seguridad.',
    },
    'usfans-comparison-guide': {
      title: 'USFans Spreadsheet frente a otros métodos: comparación',
      desc: 'Una comparación clara del enfoque de USFans Spreadsheet con la búsqueda manual y otros métodos.',
      category: 'Comparación',
      intro: [
        'Hay muchas formas de navegar y no todas son igual de eficientes.',
        'Esta guía compara la navegación organizada con otros métodos.',
      ],
      sections: [
        { heading: 'Organizado vs. desorganizado', paragraphs: ['La navegación organizada agrupa por categorías; la desorganizada requiere filtrar manualmente.'] },
        { heading: 'Búsqueda y comparación', paragraphs: ['Los catálogos estructurados facilitan buscar y comparar.'] },
        { heading: 'Qué método usar', paragraphs: ['Para búsquedas rápidas, manual; para investigación, organizado.'] },
      ],
      faq: [
        { q: '¿Cuál es la diferencia?', a: 'Organizado agrupa; desorganizado requiere filtrar.' },
        { q: '¿Cuál es más rápido?', a: 'El organizado suele ser más rápido.' },
        { q: '¿Cuándo usar búsqueda manual?', a: 'Para búsquedas rápidas.' },
      ],
      ctaTitle: 'Elige el enfoque correcto',
      ctaDesc: 'Entiende las diferencias y explora el catálogo organizado.',
    },
  },

  fr: {
    'usfans-spreadsheet-guide': {
      title: 'Guide USFans Spreadsheet : organisez et achetez tout le catalogue',
      desc: 'Un guide complet de USFans Spreadsheet : comment il organise les catégories, comment naviguer et des conseils d\'achat.',
      category: 'Guide complet',
      intro: [
        'Acheter dans un grand catalogue de mode peut être intimidant. USFans Spreadsheet a été créé pour résoudre ce problème : il rassemble chaussures, sweats, t-shirts, vestes, maillots et accessoires dans un aperçu organisé.',
        'Dans ce guide, nous expliquons ce qu\'est USFans Spreadsheet, comment fonctionne son système de catégories et comment trouver le bon article plus rapidement.',
      ],
      sections: [
        {
          heading: 'Qu\'est-ce que USFans Spreadsheet ?',
          paragraphs: [
            'C\'est un catalogue structuré qui regroupe une large gamme de mode et de produits pour fans dans des catégories claires et logiques.',
          ],
          bullets: [
            'Chaussures : baskets et chaussures décontractées',
            'Sweats/Pulls : vêtements confortables et tricotés',
            'Maillots : maillots de sport et d\'équipe',
            'Accessoires : détails qui complètent une tenue',
          ],
        },
        { heading: 'Comment naviguer efficacement', paragraphs: ['Commencez par identifier ce dont vous avez besoin. Utilisez le menu déroulant ou les boutons de catégorie pour aller directement à la section souhaitée.'] },
        { heading: 'Conseils d\'achat', paragraphs: ['Vérifiez les détails, les tailles et la disponibilité avant de commander. Consultez les informations de livraison et de retour et protégez vos données de paiement.'] },
      ],
      faq: [
        { q: 'Qu\'est-ce que USFans Spreadsheet ?', a: 'Un catalogue organisé qui regroupe la mode et les produits en catégories claires.' },
        { q: 'Comment trouver un produit rapidement ?', a: 'Utilisez le menu déroulant ou les boutons de catégorie de la page d\'accueil.' },
        { q: 'Est-ce une boutique ?', a: 'C\'est une ressource d\'information et de navigation. Les achats se font dans la boutique liée.' },
      ],
      ctaTitle: 'Prêt à explorer le catalogue ?',
      ctaDesc: 'Maintenant que vous comprenez comment cela fonctionne, explorez les catégories ou visitez la boutique.',
    },
    'usfans-shipping-cost': {
      title: 'Frais de livraison USFans : ce qu\'il faut savoir avant de commander',
      desc: 'Un guide pratique des frais de livraison USFans : comment ça marche et comment éviter les surprises.',
      category: 'Guide de livraison',
      intro: [
        'Les frais de livraison sont l\'une des questions les plus fréquentes, car ils affectent le prix final.',
        'Ce guide explique les facteurs qui influencent la livraison et comment maîtriser les coûts.',
      ],
      sections: [
        { heading: 'Ce qui affecte les frais', paragraphs: ['Le pays de destination, le poids, la taille, la méthode et les promotions.'] },
        { heading: 'Les méthodes de livraison', paragraphs: ['De la livraison standard à l\'express, les méthodes rapides coûtent plus cher.'] },
        { heading: 'Comment réduire les coûts', paragraphs: ['Regroupez les produits et cherchez les seuils de livraison gratuite.'] },
      ],
      faq: [
        { q: 'Comment sont calculés les frais ?', a: 'Selon la destination, le poids, la taille et la méthode.' },
        { q: 'Puis-je réduire les frais ?', a: 'Oui, en regroupant les produits et en cherchant la livraison gratuite.' },
        { q: 'Où voir le prix exact ?', a: 'Au moment du paiement, selon votre adresse.' },
      ],
      ctaTitle: 'Planifiez votre commande sereinement',
      ctaDesc: 'Connaissez vos options de livraison avant d\'acheter.',
    },
    'usfans-coupon': {
      title: 'Guide des coupons USFans : comment fonctionnent les réductions',
      desc: 'Apprenez comment fonctionnent les coupons et réductions USFans et comment les appliquer.',
      category: 'Guide d\'économies',
      intro: [
        'Les coupons sont un moyen populaire d\'économiser, mais seulement si vous savez où les trouver.',
        'Ce guide explique comment fonctionnent les coupons et les erreurs à éviter.',
      ],
      sections: [
        { heading: 'Comment fonctionnent les coupons', paragraphs: ['Un coupon est un code à saisir au paiement pour obtenir une réduction.'] },
        { heading: 'Où trouver des réductions', paragraphs: ['Dans les annonces officielles, les newsletters et les promotions saisonnières.'] },
        { heading: 'Erreurs courantes', paragraphs: ['Saisir un code expiré ou l\'utiliser sur des produits exclus.'] },
      ],
      faq: [
        { q: 'Comment appliquer un coupon ?', a: 'Saisissez le code dans le champ prévu au paiement.' },
        { q: 'Où trouver des coupons ?', a: 'Dans les annonces officielles et les promotions.' },
        { q: 'Pourquoi mon coupon ne fonctionne pas ?', a: 'Il peut être expiré ou soumis à conditions.' },
      ],
      ctaTitle: 'Profitez de vos économies',
      ctaDesc: 'Comprenez les réductions avant d\'acheter.',
    },
    'usfans-discount-code': {
      title: 'Codes promo USFans : économisez davantage au paiement',
      desc: 'Un guide clair des codes promo USFans : ce qu\'ils sont et comment les utiliser.',
      category: 'Guide d\'économies',
      intro: [
        'Les codes promo sont un moyen simple de réduire le coût de votre commande.',
        'Ce guide explique ce qu\'ils sont et comment les saisir correctement.',
      ],
      sections: [
        { heading: 'Qu\'est-ce qu\'un code promo ?', paragraphs: ['Un texte court qui débloque une réduction au paiement.'] },
        { heading: 'Code vs coupon', paragraphs: ['Souvent utilisés de façon interchangeable, le code désigne le texte lui-même.'] },
        { heading: 'Comment l\'appliquer', paragraphs: ['Copiez le code, saisissez-le et confirmez la réduction avant de payer.'] },
      ],
      faq: [
        { q: 'Qu\'est-ce qu\'un code promo ?', a: 'Un texte qui débloque une réduction au paiement.' },
        { q: 'Différence avec un coupon ?', a: 'Le code désigne le texte ; le coupon, la promotion.' },
        { q: 'Que faire s\'il ne fonctionne pas ?', a: 'Vérifiez les fautes et les conditions.' },
      ],
      ctaTitle: 'Débloquez vos économies',
      ctaDesc: 'Comprenez les codes avant de commander.',
    },
    'usfans-warehouse': {
      title: 'Entrepôt USFans : comment les commandes sont stockées et expédiées',
      desc: 'Découvrez comment fonctionnent l\'entrepôt et la logistique USFans et ce que cela signifie pour les délais.',
      category: 'Guide de livraison',
      intro: [
        'L\'entrepôt est l\'endroit où les produits sont stockés avant d\'être emballés et expédiés.',
        'Ce guide explique le rôle de l\'entrepôt et ce qui affecte les délais.',
      ],
      sections: [
        { heading: 'Que fait un entrepôt ?', paragraphs: ['Il stocke, organise et prépare le stock pour l\'expédition.'] },
        { heading: 'Comment les commandes sont traitées', paragraphs: ['Après le paiement, les articles sont prélevés, emballés puis expédiés.'] },
        { heading: 'Ce qui affecte la livraison', paragraphs: ['Le temps de traitement, la méthode et la distance.'] },
      ],
      faq: [
        { q: 'Qu\'est-ce qu\'un entrepôt ?', a: 'Le lieu où les produits sont stockés et préparés pour l\'expédition.' },
        { q: 'Combien de temps dure le traitement ?', a: 'Cela varie selon la boutique.' },
        { q: 'Qu\'est-ce qui affecte la livraison ?', a: 'Le traitement, la méthode et la distance.' },
      ],
      ctaTitle: 'Sachez comment votre commande avance',
      ctaDesc: 'Comprenez la logistique avant de commander.',
    },
    'usfans-legit': {
      title: 'USFans est-il légitime ? Guide pratique de sécurité et de confiance',
      desc: 'Un regard équilibré sur la légitimité d\'USFans : quoi vérifier et comment acheter en sécurité.',
      category: 'Confiance et sécurité',
      intro: [
        'Avant d\'acheter sur un site, il est naturel de se demander s\'il est légitime.',
        'Ce guide propose des étapes pratiques pour évaluer la confiance et repérer les signaux d\'alerte.',
      ],
      sections: [
        { heading: 'Que vérifier avant d\'acheter', paragraphs: ['Des coordonnées claires, des politiques transparentes et des paiements sécurisés.'] },
        { heading: 'Signaux d\'alerte', paragraphs: ['Prix irréalistes et politiques absentes.'] },
        { heading: 'Comment acheter en sécurité', paragraphs: ['Paiements sécurisés et vérification des détails.'] },
      ],
      faq: [
        { q: 'Le site est-il légitime ?', a: 'Nous ne faisons pas d\'affirmations sans preuve ; vérifiez vous-même.' },
        { q: 'Quels sont les signaux d\'alerte ?', a: 'Prix irréalistes et politiques absentes.' },
        { q: 'Comment acheter en sécurité ?', a: 'Paiements sécurisés et vérification des détails.' },
      ],
      ctaTitle: 'Achetez en toute confiance',
      ctaDesc: 'Appliquez des vérifications pratiques avant d\'acheter.',
    },
    'usfans-review': {
      title: 'Avis USFans : fonctionnalités, catalogue et à quoi s\'attendre',
      desc: 'Un aperçu honnête de l\'expérience USFans : le catalogue, les catégories et ce qu\'il faut savoir.',
      category: 'Avis',
      intro: [
        'Un bon avis aide à comprendre ce qu\'offre une boutique et comment elle est organisée.',
        'Nous passons en revue la structure du catalogue et les principales catégories.',
      ],
      sections: [
        { heading: 'Le catalogue en un coup d\'œil', paragraphs: ['Il couvre une large gamme de mode, organisée en catégories claires.'] },
        { heading: 'Comment il est organisé', paragraphs: ['Les produits sont regroupés par catégorie.'] },
        { heading: 'Points à retenir', paragraphs: ['Vérifiez les détails, la livraison et les retours avant de commander.'] },
      ],
      faq: [
        { q: 'Que contient le catalogue ?', a: 'Chaussures, sweats, t-shirts, maillots, accessoires et plus.' },
        { q: 'Comment est-il organisé ?', a: 'En catégories claires.' },
        { q: 'Que vérifier avant de commander ?', a: 'Détails, livraison et retours.' },
      ],
      ctaTitle: 'Voyez le catalogue vous-même',
      ctaDesc: 'Explorez les catégories ou visitez la boutique.',
    },
    'usfans-creator-list': {
      title: 'Liste des créateurs USFans : comment organiser et naviguer',
      desc: 'Un guide des listes de créateurs USFans : comment elles sont organisées et comment évaluer l\'information.',
      category: 'Guide des créateurs',
      intro: [
        'Les listes de créateurs aident à découvrir et organiser l\'information en un seul endroit.',
        'Ce guide explique ce qu\'est une liste de créateurs et comment évaluer l\'information.',
      ],
      sections: [
        { heading: 'Qu\'est-ce qu\'une liste de créateurs ?', paragraphs: ['Une collection structurée d\'informations sur les créateurs.'] },
        { heading: 'Comment la parcourir', paragraphs: ['Identifiez le type de créateur et utilisez les filtres.'] },
        { heading: 'Comment évaluer l\'information', paragraphs: ['Traitez les entrées comme un point de départ et vérifiez.'] },
      ],
      faq: [
        { q: 'Qu\'est-ce qu\'une liste de créateurs ?', a: 'Une collection structurée d\'informations sur les créateurs.' },
        { q: 'Comment la parcourir ?', a: 'Utilisez les catégories ou les filtres.' },
        { q: 'Faut-il faire confiance à chaque entrée ?', a: 'Traitez-les comme un point de départ.' },
      ],
      ctaTitle: 'Naviguez parmi les créateurs facilement',
      ctaDesc: 'Comprenez comment fonctionnent les listes de créateurs.',
    },
    'usfans-spreadsheet-search': {
      title: 'Recherche USFans Spreadsheet : comment trouver plus vite',
      desc: 'Des conseils pratiques pour chercher dans USFans Spreadsheet et trouver des produits plus vite.',
      category: 'Guide de recherche',
      intro: [
        'Trouver un produit précis est plus rapide quand on sait chercher efficacement.',
        'Ce guide partage des conseils de recherche et les erreurs courantes.',
      ],
      sections: [
        { heading: 'Commencez par la bonne catégorie', paragraphs: ['Allez directement à la bonne catégorie, puis affinez.'] },
        { heading: 'Utilisez des termes précis', paragraphs: ['Soyez précis sur le type, le style ou la couleur.'] },
        { heading: 'Erreurs courantes', paragraphs: ['Des termes trop larges et des filtres ignorés.'] },
      ],
      faq: [
        { q: 'Comment trouver plus vite ?', a: 'Allez à la bonne catégorie et utilisez des termes précis.' },
        { q: 'Pourquoi mes recherches sont larges ?', a: 'Des termes vagues donnent trop de résultats.' },
        { q: 'Comment affiner les résultats ?', a: 'Utilisez les filtres de catégorie.' },
      ],
      ctaTitle: 'Trouvez ce qu\'il vous faut plus vite',
      ctaDesc: 'Adoptez de meilleures habitudes de recherche.',
    },
    'usfans-safety-guide': {
      title: 'Guide de sécurité USFans : comment acheter en ligne en sécurité',
      desc: 'Un guide pratique de sécurité pour les achats en ligne : protéger ses données et éviter les risques.',
      category: 'Guide de sécurité',
      intro: [
        'Acheter en ligne est pratique, mais il faut rester attentif aux risques.',
        'Ce guide couvre les bonnes pratiques et la détection des activités suspectes.',
      ],
      sections: [
        { heading: 'Protégez vos données', paragraphs: ['Ne partagez jamais vos mots de passe avec des sites inconnus.'] },
        { heading: 'Repérez les liens suspects', paragraphs: ['Vérifiez les liens et évitez les téléchargements inattendus.'] },
        { heading: 'Que faire si quelque chose semble étrange', paragraphs: ['Arrêtez, vérifiez le site et contactez la boutique.'] },
      ],
      faq: [
        { q: 'Comment protéger mes données ?', a: 'Utilisez des mots de passe forts et uniques.' },
        { q: 'Comment repérer un lien suspect ?', a: 'Vérifiez les liens avant de cliquer.' },
        { q: 'Que faire si quelque chose semble étrange ?', a: 'Arrêtez et contactez la boutique.' },
      ],
      ctaTitle: 'Restez en sécurité en achetant',
      ctaDesc: 'Gardez ces bonnes pratiques à l\'esprit.',
    },
    'usfans-comparison-guide': {
      title: 'USFans Spreadsheet face aux autres méthodes : comparaison',
      desc: 'Une comparaison claire de l\'approche USFans Spreadsheet avec la recherche manuelle.',
      category: 'Comparaison',
      intro: [
        'Il existe de nombreuses façons de naviguer, et toutes ne se valent pas.',
        'Ce guide compare la navigation organisée aux autres méthodes.',
      ],
      sections: [
        { heading: 'Organisé vs désorganisé', paragraphs: ['La navigation organisée regroupe par catégories ; la désorganisée exige un tri manuel.'] },
        { heading: 'Recherche et comparaison', paragraphs: ['Les catalogues structurés facilitent la recherche et la comparaison.'] },
        { heading: 'Quelle méthode utiliser ?', paragraphs: ['Pour les recherches rapides, manuelle ; pour la recherche, organisée.'] },
      ],
      faq: [
        { q: 'Quelle est la différence ?', a: 'Organisé regroupe ; désorganisé exige un tri.' },
        { q: 'Laquelle est plus rapide ?', a: 'L\'organisée est généralement plus rapide.' },
        { q: 'Quand utiliser la recherche manuelle ?', a: 'Pour les recherches rapides.' },
      ],
      ctaTitle: 'Choisissez la bonne approche',
      ctaDesc: 'Comprenez les différences et explorez le catalogue organisé.',
    },
  },
};